import { useState } from "react";
import { Undo2, Trash2 } from "lucide-react";
import { useAuth } from "@/app/auth";
import { useTrash, useTrashRestore } from "./delete";
import { dateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ENTITY_LABEL: Record<string, string> = {
  client: "Client", contact: "Interlocuteur", product: "Produit", client_price: "Tarif négocié",
  quote: "Devis", order: "Commande", contract: "Contrat", lead: "Lead", invoice: "Facture",
  dispute: "Contestation", delivery: "Livraison", contract_template: "Modèle de contrat",
  tax_regime: "Régime fiscal", fee_type: "Type de frais",
};
const days = (d: string) => Math.max(0, Math.ceil((new Date(d).getTime() - Date.now()) / 86400000));

export function TrashTab() {
  const { roles } = useAuth();
  const isAdmin = roles.includes("admin");
  const { data, isLoading } = useTrash();
  const restore = useTrashRestore();
  const [confirm, setConfirm] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Ce qui a été supprimé définitivement reste ici <strong>30 jours</strong>, avec tout son contenu : un client revient avec ses interlocuteurs et ses tarifs, un devis avec ses lignes. Passé ce délai, la copie est effacée pour de bon, chaque nuit.
      </p>

      <div className="glass overflow-x-auto rounded-2xl p-1" data-testid="trash-table">
        <Table>
          <TableHeader><TableRow><TableHead>Élément</TableHead><TableHead>Motif</TableHead><TableHead>Supprimé par</TableHead><TableHead>Le</TableHead><TableHead>Effacement</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={6} className="text-muted-foreground">Chargement…</TableCell></TableRow>
              : !data?.length ? <TableRow><TableCell colSpan={6} className="text-muted-foreground">La corbeille est vide.</TableCell></TableRow>
              : data.map((t) => (
                <TableRow key={t.id} className={t.restored_at ? "opacity-60" : ""}>
                  <TableCell>
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-muted-foreground">{ENTITY_LABEL[t.entity] ?? t.entity} · {t.rows} ligne{t.rows > 1 ? "s" : ""} conservée{t.rows > 1 ? "s" : ""}</div>
                  </TableCell>
                  <TableCell className="max-w-xs text-sm text-muted-foreground">{t.reason ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.by ?? "—"}</TableCell>
                  <TableCell className="text-sm">{dateTime(t.deleted_at)}</TableCell>
                  <TableCell>{t.restored_at ? <Badge variant="secondary">Restauré</Badge> : <span className="text-sm text-muted-foreground">dans {days(t.purge_after)} j</span>}</TableCell>
                  <TableCell className="text-right">
                    {t.restored_at ? <span className="text-xs text-muted-foreground">{dateTime(t.restored_at)}</span>
                      : !isAdmin ? <span className="text-xs text-muted-foreground">admin seulement</span>
                      : confirm === t.id ? (
                        <span className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Non</Button>
                          <Button size="sm" disabled={restore.isPending} onClick={() => restore.mutate(t.id, { onSuccess: () => setConfirm(null) })}>Restaurer</Button>
                        </span>
                      ) : <Button size="sm" variant="outline" onClick={() => setConfirm(t.id)}><Undo2 className="mr-1.5 h-3.5 w-3.5" />Restaurer</Button>}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Trash2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Ce qui n'apparaît jamais ici : les factures et avoirs émis, les paiements encaissés, les contrats signés et les ventes de leads. Ces pièces ne se suppriment pas — elles s'annulent, et l'annulation reste visible sur le document.
      </p>
    </div>
  );
}
